import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from "chart.js"

// Register Chart.js components
Chart.register(...registerables)

export default class extends Controller {
  static targets = ["pieCanvas", "barCanvas"]
  static values = {
    aggregateData: Object,
    chartConfig: { type: Object, default: {} }
  }

  connect() {
    // Wait for targets to be available and data to be loaded
    if (this.hasPieCanvasTarget && this.hasAggregateDataValue) {
      this.renderPieChart()
    }

    if (this.hasBarCanvasTarget && this.hasAggregateDataValue) {
      this.renderBarChart()
    }
  }

  disconnect() {
    // Cleanup: Destroy Chart.js instances to prevent memory leaks
    if (this.pieChart) {
      this.pieChart.destroy()
      this.pieChart = null
    }

    if (this.barChart) {
      this.barChart.destroy()
      this.barChart = null
    }
  }

  resolvedChartConfig() {
    const defaults = { chart_kind: 'pie', primary_color: '#003d68', secondary_color: '#e5e7eb', label_style: 'percentage' }
    return Object.assign({}, defaults, this.chartConfigValue)
  }

  buildColors(count) {
    const config = this.resolvedChartConfig()
    const fallback = [
      'rgba(59, 130, 246, 0.8)',
      'rgba(16, 185, 129, 0.8)',
      'rgba(251, 146, 60, 0.8)',
      'rgba(239, 68, 68, 0.8)',
      'rgba(168, 85, 247, 0.8)',
      'rgba(236, 72, 153, 0.8)',
    ]
    if (count <= 2) {
      return [config.primary_color, config.secondary_color].slice(0, count)
    }
    fallback[0] = config.primary_color
    fallback[1] = config.secondary_color
    return fallback.slice(0, count)
  }

  renderPieChart() {
    const ctx = this.pieCanvasTarget.getContext('2d')
    const data = this.aggregateDataValue.grouped_data.by_response_option
    const config = this.resolvedChartConfig()

    // Convert hash to Chart.js format
    const labels = Object.keys(data)
    const values = Object.values(data)

    // Don't render if no data
    if (labels.length === 0) {
      return
    }

    this.pieChart = new Chart(ctx, {
      type: config.chart_kind === 'doughnut' ? 'doughnut' : 'pie',
      data: {
        labels: labels,
        datasets: [{
          label: 'Response Count',
          data: values,
          backgroundColor: this.buildColors(labels.length),
          borderWidth: 2,
          borderColor: '#ffffff'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              padding: 15,
              font: {
                size: 12
              }
            }
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                const label = context.label || ''
                const value = context.parsed || 0
                const total = context.dataset.data.reduce((a, b) => a + b, 0)
                const percentage = ((value / total) * 100).toFixed(1)
                return `${label}: ${value} (${percentage}%)`
              }
            }
          }
        }
      }
    })
  }

  renderBarChart() {
    const ctx = this.barCanvasTarget.getContext('2d')
    // Reports::ObjectiveAggregator#build_grouped_data returns `by_cycle`, not
    // `by_time_period` -- this key never existed on the aggregator's output
    // (confirmed by a full-repo grep), so this chart silently never rendered
    // any data on ANY objective show page before this fix. Found while
    // building the objectives chart-capture pipeline (Objectives::ChartCapture) --
    // a capture whose bar chart is permanently blank would defeat the whole
    // point of embedding it, so fixed here rather than shipping around it.
    const data = this.aggregateDataValue.grouped_data.by_cycle || {}

    // Convert time series data to Chart.js format
    const labels = Object.keys(data)
    const values = Object.values(data)

    // Don't render if no data
    if (labels.length === 0) {
      return
    }

    this.barChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [{
          label: 'Mission Tasks Completed',
          data: values,
          backgroundColor: 'rgba(59, 130, 246, 0.8)',  // blue-500
          borderColor: 'rgba(59, 130, 246, 1)',
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              precision: 0  // Whole numbers only
            },
            title: {
              display: true,
              text: 'Count'
            }
          },
          x: {
            title: {
              display: true,
              text: 'Date'
            }
          }
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              title: function(context) {
                return `Date: ${context[0].label}`
              },
              label: function(context) {
                return `Completed: ${context.parsed.y}`
              }
            }
          }
        }
      }
    })
  }
}
